import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import SelectDrawers from '../selectDrawers/selectDrawers';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { FiTrash2 } from 'react-icons/fi';
import { IoAddSharp } from 'react-icons/io5';
import { normalizeData } from './normalizeData';

interface Key {
  type_key_id: string;
  quantity: number;
  serial_number: string;
}

const FormKeyset = ({ onSubmit }: any) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [keys, setKeys] = useState<Key[]>([
    { type_key_id: '', quantity: 1, serial_number: '' },
  ]);
  const { type_keys } = useSelector((state: any) => state.type_keys);
  const property = useSelector(
    (state: any) => state.properties.properties.properties[0]
  );
  const [hasSerialNumber, setHasSerialNumber] = useState(true);

  const formik = useFormik({
    initialValues: {
      drawers_id: '',
      keyset_quantity: 1,
    },
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      if (!values.drawers_id) {
        errors.drawers_id = '* Requerido';
      }

      if (!values.keyset_quantity && values.keyset_quantity !== 0) {
        errors.keyset_quantity = '* Requerido';
      }

      return errors;
    },
    onSubmit: (values) => {
      const dataToSend = normalizeData({
        type_keys,
        property,
        formData: { ...values, keys },
      });
      setIsDisabled(true);
      onSubmit(dataToSend);
    },
  });

  const handleAddKey = () => {
    const newKey: Key = { type_key_id: '', quantity: 1, serial_number: '' };
    setKeys([...keys, newKey]);
  };

  const handleDeleteKey = (index: number) => {
    const updatedKeys = [...keys];
    if (updatedKeys.length === 1) {
      return;
    }

    updatedKeys.splice(index, 1);
    setKeys(updatedKeys);
  };

  const hasElectronicKeyWithoutSerial = keys.some(
    (key) => String(key.type_key_id) === '9' && !hasSerialNumber
  );

  return (
    <div>
      <SelectDrawers property={property} formik={formik} />

      <hr className='my-9' />

      <h4 className='font-bold mb-5'>Personaliza la creación de llaves:</h4>

      <form onSubmit={formik.handleSubmit}>
        <div className='flex items-center'>
          <h5 className='block flex-shrink-0 mr-4 text-sm font-medium text-gray-900 darkNight:text-white'>
            Juego de llaves:
          </h5>
          <div>
            <FormControl variant='outlined'>
              <InputLabel>Cantidad</InputLabel>
              <Select
                className='w-44'
                id='form-keyset_quantity'
                name='keyset_quantity'
                value={formik.values.keyset_quantity}
                onChange={formik.handleChange}
                error={!!formik.errors.keyset_quantity}
                label='Selecciona cantidad'
              >
                <MenuItem value=''>
                  <em>Selecciona cantidad</em>
                </MenuItem>

                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
              </Select>
            </FormControl>

            {formik.errors.keyset_quantity && (
              <p className='text-red-600 text-sm mt-0.5'>
                {formik.errors.keyset_quantity}
              </p>
            )}
          </div>
        </div>

        {keys.map((key, index: number) => (
          <div
            key={keys[index].type_key_id}
            className='bg-slate-100 rounded-xl p-4 mt-4'
          >
            <div className='flex items-center '>
              <div>
                <h4 className='mb-3 block flex-shrink-0 mr-4 text-sm font-medium text-gray-900 darkNight:text-white'>
                  Seleccionar tipo de llave:
                </h4>
                <FormControl className='bg-white' variant='outlined'>
                  <Select
                    className='w-44'
                    id='form-typekey'
                    name='typekey'
                    value={keys[index].type_key_id}
                    onChange={(e) => {
                      const updatedKeys = [...keys];
                      const value = e.target.value;
                      if (value === 9) updatedKeys[index].quantity = 1;
                      (updatedKeys[index] as any).type_key_id = e.target.value;
                      setKeys(updatedKeys);
                    }}
                  >
                    <MenuItem value=''>
                      <em>Selecciona el tipo de llave</em>
                    </MenuItem>
                    {type_keys.slice(1)?.map((type_key: any) => (
                      <MenuItem key={type_key.id} value={type_key.id}>
                        {type_key.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className='ml-8'>
                <h4 className='mb-3 block flex-shrink-0 mr-4 text-sm font-medium text-gray-900 darkNight:text-white'>
                  Cantidad:
                </h4>
                <FormControl className='bg-white' variant='outlined'>
                  <Select
                    className='w-32'
                    id='form-typekeyQuantity'
                    name='typekeyQuantity'
                    value={keys[index].quantity}
                    disabled={String(key.type_key_id) === '9'}
                    onChange={(e) => {
                      if (String(key.type_key_id) === '9') return;
                      const updatedKeys = [...keys];
                      updatedKeys[index].quantity = parseInt(
                        e.target.value as any
                      );
                      setKeys(updatedKeys);
                    }}
                  >
                    <MenuItem value=''>
                      <em>Selecciona Cantidad</em>
                    </MenuItem>
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={5}>5</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <button
                data-testid='delete-button'
                type='button'
                className='text-red-600 font-medium ml-5'
                onClick={() => handleDeleteKey(index)}
              >
                <FiTrash2 className='text-xl' />
              </button>
            </div>

            {String(key.type_key_id) === '9' && (
              <div className='mt-4'>
                <h4 className='mb-3 block flex-shrink-0 mr-4 text-sm font-medium text-gray-900 darkNight:text-white'>
                  Captura el número serial:
                </h4>
                <div className='bg-white'>
                  <TextField
                    fullWidth
                    id='outlined-basic'
                    label=''
                    variant='outlined'
                    placeholder='Número serial'
                    onChange={(e) => {
                      const val = e.target.value;
                      const updatedKeys = [...keys];
                      updatedKeys[index].serial_number = val.replace(/\s/g, '');
                      setHasSerialNumber(Boolean(val.trim()));
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <div
          onClick={handleAddKey}
          className='bg-slate-100 rounded-xl p-4 mt-4 cursor-pointer flex items-center justify-center'
        >
          <button
            type='button'
            className='flex items-center justify-center text-sm'
          >
            <IoAddSharp className='text-2xl mr-1' />
            Agregar llave
          </button>
        </div>

        <div className='w-full mt-12'>
          {!!Object.keys(formik.errors).length && (
            <div
              className='flex p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 darkNot:bg-gray-800 darkNot:text-red-400'
              role='alert'
            >
              <svg
                aria-hidden='true'
                className='flex-shrink-0 inline w-5 h-5 mr-3'
                fill='currentColor'
                viewBox='0 0 20 20'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
              <span className='sr-only'>Info</span>
              <div>
                <span className='font-medium'>
                  Aún hay campos por responder, por favor complétalos para poder
                  crear las llaves.
                </span>
              </div>
            </div>
          )}
          <button
            disabled={
              !property ||
              keys.some((key) => !key.type_key_id) ||
              isDisabled ||
              hasElectronicKeyWithoutSerial
            }
            className='w-full h-12 disabled:bg-gray-400 text-white flex items-center justify-center bg-purple-600 focus:outline-none font-medium rounded-lg px-4 py-2 darkNot:bg-purple-600 darkNot:hover:bg-purple-700 darkNot:focus:ring-purple-800'
          >
            Guardar
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormKeyset;
